/**
 * Shopify OAuth authentication helper
 */

export interface ShopifyOAuthConfig {
  shopDomain: string // Required for Shopify OAuth
  clientId: string // Custom app API key
  clientSecret: string // Custom app API secret
}

export interface ShopifyOAuthResult {
  success: boolean
  error?: string
}

/**
 * Initiates Shopify OAuth flow in a popup window
 * @param config Must include shopDomain, clientId, and clientSecret for custom app
 */
export async function initiateShopifyOAuth(
  config: ShopifyOAuthConfig
): Promise<ShopifyOAuthResult> {
  return new Promise((resolve) => {
    const { shopDomain, clientId, clientSecret } = config

    if (!shopDomain) {
      resolve({
        success: false,
        error: 'Shop domain is required for Shopify OAuth',
      })
      return
    }

    if (!clientId || !clientSecret) {
      resolve({
        success: false,
        error: 'Custom app clientId and clientSecret are required for Shopify OAuth',
      })
      return
    }

    // Build authorization URL with custom app credentials
    const params = new URLSearchParams({
      shopDomain: shopDomain,
      clientId: clientId,
      clientSecret: clientSecret,
    })
    const authUrl = `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/oauth/shopify/authorize?${params.toString()}`

    // Open popup window
    const width = 600
    const height = 700
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(
      authUrl,
      'Shopify OAuth',
      `width=${width},height=${height},left=${left},top=${top}`
    )

    if (!popup) {
      resolve({
        success: false,
        error: 'Failed to open popup window. Please allow popups for this site.',
      })
      return
    }

    let isCompleted = false // Flag to prevent race condition

    // Handle popup close without completion
    let checkPopupClosed: NodeJS.Timeout | null = null
    const channel = new BroadcastChannel('oauth_channel')

    const cleanup = () => {
      if (checkPopupClosed) {
        clearInterval(checkPopupClosed)
        checkPopupClosed = null
      }
      channel.close()
    }

    // Listen for messages via BroadcastChannel (works even when window.opener is lost)
    channel.onmessage = (event) => {
      const data = event.data

      // Only handle Shopify OAuth messages
      if (!data || !data.type || !data.type.startsWith('SHOPIFY_OAUTH_')) {
        return
      }
      // Note: Don't access popup.location.href - it causes CORS SecurityError after redirect

      if (data.type === 'SHOPIFY_OAUTH_SUCCESS') {
        isCompleted = true
        cleanup()
        // Close the popup manually to prevent race condition
        if (!popup.closed) {
          popup.close()
        }
        resolve({ success: true })
      } else if (data.type === 'SHOPIFY_OAUTH_ERROR') {
        isCompleted = true
        cleanup()
        // Close the popup manually to prevent race condition
        if (!popup.closed) {
          popup.close()
        }
        resolve({
          success: false,
          error: data.error || 'OAuth authentication failed',
        })
      }
    }

    checkPopupClosed = setInterval(() => {
      // Skip popup closed check if OAuth has already completed
      if (isCompleted) {
        return
      }

      try {
        // Check if popup is closed - wrap in try/catch as accessing .closed can throw on cross-origin
        if (popup.closed) {
          // Give BroadcastChannel message more time to arrive (increased for hot reload interference)
          setTimeout(() => {
            if (!isCompleted) {
              cleanup()
              resolve({
                success: false,
                error: 'OAuth window was closed before completing authentication',
              })
            }
          }, 1000) // Increased delay to handle hot reload interference
        }
      } catch {
        // If we can't access popup.closed (cross-origin), just continue checking
      }
    }, 1500)
  })
}
